import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../../components/ui/card';
import { DataTable, DataTableColumn } from '../../../components/ui/data-table';
import StatsCard from '../../../components/common/StatsCard';
import PerformanceCard from '../../../components/trading/PerformanceCard';
import {
  Users,
  DollarSign,
  TrendingUp,
  Activity,
  Copy,
  PieChart,
} from 'lucide-react';
import PageHeader from '../../../components/common/PageHeader';

const CopyTradingDashboard = () => {
  const recentStrategies = [
    {
      id: 'CSTR001',
      name: 'Gold Scalper Pro',
      trader: 'John Smith',
      roi: 42.5,
      totalFollowers: 45,
      totalCopied: 125000,
      status: 'active',
      riskLevel: 'High',
      monthlyProfit: 9.2,
    },
    {
      id: 'CSTR002',
      name: 'Forex Trend Master',
      trader: 'Jane Doe',
      roi: 35.8,
      totalFollowers: 38,
      totalCopied: 98000,
      status: 'active',
      riskLevel: 'Medium',
      monthlyProfit: 6.5,
    },
    {
      id: 'CSTR003',
      name: 'Conservative Growth',
      trader: 'Bob Johnson',
      roi: 18.2,
      totalFollowers: 22,
      totalCopied: 75000,
      status: 'pending',
      riskLevel: 'Low',
      monthlyProfit: 3.1,
    },
    {
      id: 'CSTR004',
      name: 'Crypto Alpha',
      trader: 'Mike Wilson',
      roi: 48.3,
      totalFollowers: 52,
      totalCopied: 145000,
      status: 'active',
      riskLevel: 'High',
      monthlyProfit: 11.5,
    },
    {
      id: 'CSTR005',
      name: 'Index Fund Pro',
      trader: 'Sarah Davis',
      roi: 20.7,
      totalFollowers: 28,
      totalCopied: 88000,
      status: 'active',
      riskLevel: 'Low',
      monthlyProfit: 3.8,
    },
  ];

  const strategyColumns: DataTableColumn<(typeof recentStrategies)[0]>[] = [
    {
      id: 'id',
      header: 'Strategy ID',
      accessor: row => <span className="font-medium">{row.id}</span>,
      sortable: true,
      sortValue: row => row.id,
      width: '120px',
    },
    {
      id: 'name',
      header: 'Strategy Name',
      accessor: row => row.name,
      sortable: true,
      sortValue: row => row.name,
      width: '180px',
    },
    {
      id: 'trader',
      header: 'Trader',
      accessor: row => row.trader,
      sortable: true,
      sortValue: row => row.trader,
      width: '150px',
    },
    {
      id: 'roi',
      header: 'ROI',
      accessor: row => (
        <span className="text-green-600 font-semibold">
          {row.roi.toFixed(2)}%
        </span>
      ),
      sortable: true,
      sortValue: row => row.roi,
      width: '100px',
    },
    {
      id: 'monthlyProfit',
      header: 'Monthly Profit',
      accessor: row => (
        <span className="text-green-600 font-semibold">
          {row.monthlyProfit.toFixed(2)}%
        </span>
      ),
      sortable: true,
      sortValue: row => row.monthlyProfit,
      width: '120px',
    },
    {
      id: 'totalFollowers',
      header: 'Followers',
      accessor: row => row.totalFollowers,
      sortable: true,
      sortValue: row => row.totalFollowers,
      width: '120px',
    },
    {
      id: 'totalCopied',
      header: 'Total Copied',
      accessor: row => `$${row.totalCopied.toLocaleString()}`,
      sortable: true,
      sortValue: row => row.totalCopied,
      width: '140px',
    },
    {
      id: 'riskLevel',
      header: 'Risk',
      accessor: row => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.riskLevel === 'High'
              ? 'bg-red-100 text-red-800'
              : row.riskLevel === 'Medium'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-green-100 text-green-800'
          }`}
        >
          {row.riskLevel}
        </span>
      ),
      sortable: true,
      sortValue: row => row.riskLevel,
      width: '100px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.status === 'active'
              ? 'bg-green-100 text-green-800'
              : row.status === 'pending'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-gray-100 text-gray-800'
          }`}
        >
          {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.status,
      width: '120px',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Copy Trading Dashboard"
          description="Overview of Copy Trading Module"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Strategies"
          value={recentStrategies.length.toString()}
          change="+4 this month"
          icon={PieChart}
        />

        <StatsCard
          title="Total Followers"
          value={recentStrategies
            .reduce((sum, s) => sum + s.totalFollowers, 0)
            .toString()}
          change="+18% from last month"
          icon={Users}
        />

        <StatsCard
          title="Total Copied"
          value={`$${(recentStrategies.reduce((sum, s) => sum + s.totalCopied, 0) / 1000000).toFixed(2)}M`}
          change="+22% from last month"
          icon={DollarSign}
        />

        <StatsCard
          title="Active Strategies"
          value={recentStrategies
            .filter(s => s.status === 'active')
            .length.toString()}
          change="+2 this week"
          icon={Activity}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <PerformanceCard
          title="Average ROI"
          value={`${(recentStrategies.reduce((sum, s) => sum + s.roi, 0) / recentStrategies.length).toFixed(2)}%`}
          change="+6.2% from last month"
          changeType="positive"
          icon={TrendingUp}
          description="Across all strategies"
        />

        <PerformanceCard
          title="Total Profit Distributed"
          value="$520,000"
          change="+18% from last month"
          changeType="positive"
          icon={DollarSign}
          description="This quarter"
        />

        <PerformanceCard
          title="Pending Approvals"
          value={recentStrategies
            .filter(s => s.status === 'pending')
            .length.toString()}
          change="Requires attention"
          changeType="neutral"
          icon={Copy}
          description="Strategy applications"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent Strategies</CardTitle>
          <CardDescription>
            Latest copy trading strategies added to the platform
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={strategyColumns}
            data={recentStrategies}
            searchPlaceholder="Search strategies..."
            showColumnVisibility={true}
            showExportButton={false}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default CopyTradingDashboard;
